import { Container, Graphics, Text, TextStyle, BlurFilter } from 'pixi.js';
import type { Agent, AgentStatus } from '@/types/agent';
import { hexToNum } from '@/utils/colors';

const SPRITE_SIZE = 20; // diamètre du sprite agent
const SPEED_PPS = 90; // pixels par seconde

const STATUS_PULSE_COLORS: Partial<Record<AgentStatus, number>> = {
  waiting_validation: 0xFFB300,
  error: 0xFF1744,
  meeting: 0x7C4DFF,
};

export class AgentSprite {
  container: Container;
  private body: Graphics;
  private glow: Graphics;
  private statusIcon: Container;
  private nameLabel: Text;
  private pulseTimer = 0;
  private agent: Agent;

  // Position courante en pixels (interpolée à 60fps)
  private curX: number;
  private curY: number;
  private pathQueue: Array<{ x: number; y: number }> = [];

  constructor(agent: Agent) {
    this.agent = agent;
    this.curX = agent.position.x;
    this.curY = agent.position.y;

    this.container = new Container();
    this.container.x = this.curX;
    this.container.y = this.curY;

    const color = hexToNum(agent.color);

    // ── Glow (flou doux autour du sprite) ────────────────────────────
    this.glow = new Graphics();
    this.glow.circle(0, 0, SPRITE_SIZE * 0.9).fill({ color, alpha: 0.25 });
    const blur = new BlurFilter({ strength: 6, quality: 2 });
    this.glow.filters = [blur];
    this.container.addChild(this.glow);

    // ── Corps principal ───────────────────────────────────────────────
    this.body = new Graphics();
    this.container.addChild(this.body);

    // ── Icône d'état ──────────────────────────────────────────────────
    this.statusIcon = new Container();
    this.container.addChild(this.statusIcon);

    // ── Label nom ─────────────────────────────────────────────────────
    const style = new TextStyle({
      fontFamily: '"Press Start 2P", monospace',
      fontSize: 5,
      fill: color,
      dropShadow: { color: 0x000000, blur: 4, distance: 0, alpha: 0.8 },
    });
    this.nameLabel = new Text({ text: agent.codename, style });
    this.nameLabel.anchor.set(0.5, 0);
    this.nameLabel.y = SPRITE_SIZE + 3;
    this.container.addChild(this.nameLabel);

    this.redrawBody();
    this.redrawStatusIcon();

    // Interactivité
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
  }

  /** Appelé par le moteur Pixi à 60fps (animation seulement, sans lecture du store) */
  tick(deltaMs: number) {
    this.moveAlongPath(deltaMs);
    this.pulseTimer += deltaMs / 1000;
    this.animatePulse();
  }

  private moveAlongPath(deltaMs: number) {
    if (this.pathQueue.length === 0) return;

    const target = this.pathQueue[0];
    const dx = target.x - this.curX;
    const dy = target.y - this.curY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const moveAmount = (SPEED_PPS * deltaMs) / 1000;

    if (dist < 2 || dist <= moveAmount) {
      this.curX = target.x;
      this.curY = target.y;
      this.pathQueue.shift();
    } else {
      this.curX += (dx / dist) * moveAmount;
      this.curY += (dy / dist) * moveAmount;
    }

    this.container.x = Math.round(this.curX);
    this.container.y = Math.round(this.curY);
  }

  private animatePulse() {
    const pulse = Math.sin(this.pulseTimer * 2.5) * 0.5 + 0.5; // 0-1

    const pulseColor = STATUS_PULSE_COLORS[this.agent.status];
    if (pulseColor) {
      this.glow.alpha = 0.3 + pulse * 0.4;
    } else if (this.agent.status === 'working') {
      this.glow.alpha = 0.2 + pulse * 0.2;
    } else {
      this.glow.alpha = 0.15;
    }

    // Légère oscillation du corps en mode idle/thinking
    if (this.agent.status === 'idle' || this.agent.status === 'thinking') {
      this.body.y = Math.round(Math.sin(this.pulseTimer * 1.5) * 1.5);
    } else {
      this.body.y = 0;
    }
  }

  syncState(agent: Agent) {
    if (agent.status !== this.agent.status) {
      this.redrawBody();
      this.redrawStatusIcon();
    }
    if (agent.pixelPath.length > 0 && this.pathQueue.length === 0) {
      this.pathQueue = [...agent.pixelPath];
    }
    this.agent = agent;
  }

  private redrawBody() {
    this.body.clear();
    const { status, color } = this.agent;
    const c = hexToNum(color);
    const half = SPRITE_SIZE / 2;

    // Ombre
    this.body.circle(1, 2, half - 1).fill({ color: 0x000000, alpha: 0.5 });

    // Corps principal (cercle néon)
    this.body.circle(0, 0, half).fill({ color: c, alpha: status === 'offline' ? 0.3 : 0.9 });

    // Cercle intérieur (reflet)
    this.body.circle(-2, -3, half * 0.45).fill({ color: 0xffffff, alpha: 0.12 });

    // Indicateur de direction
    const facing = this.agent.position.facing;
    const dirOffset = half - 3;
    const dirs = { up: [0, -dirOffset], down: [0, dirOffset], left: [-dirOffset, 0], right: [dirOffset, 0] };
    const [ox, oy] = dirs[facing] ?? [0, -dirOffset];
    this.body.circle(ox, oy, 3).fill({ color: 0xffffff, alpha: 0.7 });

    // Badge de statut (anneau coloré)
    const statusColors: Partial<Record<AgentStatus, number>> = {
      working: 0x00E676,
      idle: 0x4a5568,
      thinking: 0xE040FB,
      waiting_validation: 0xFFB300,
      error: 0xFF1744,
      meeting: 0x7C4DFF,
    };
    const sc = statusColors[status];
    if (sc) {
      this.body.circle(0, 0, half + 2).stroke({ width: 2, color: sc, alpha: 0.8 });
    }
  }

  private redrawStatusIcon() {
    this.statusIcon.removeChildren();
    const { status } = this.agent;

    if (status === 'waiting_validation') {
      // Point d'exclamation orange pulsant au-dessus
      const icon = new Graphics();
      icon.circle(0, 0, 5).fill({ color: 0xFFB300 });
      icon.y = -(SPRITE_SIZE + 8);
      const textStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 7, fill: 0x000000, fontWeight: 'bold' });
      const t = new Text({ text: '!', style: textStyle });
      t.anchor.set(0.5, 0.5);
      t.y = -(SPRITE_SIZE + 8);
      this.statusIcon.addChild(icon, t);
    } else if (status === 'thinking') {
      // Ellipsis de réflexion
      const style = new TextStyle({ fontFamily: 'monospace', fontSize: 8, fill: 0xE040FB });
      const t = new Text({ text: '···', style });
      t.alpha = 0.9;
      t.anchor.set(0.5, 0.5);
      t.y = -(SPRITE_SIZE + 6);
      this.statusIcon.addChild(t);
    } else if (status === 'meeting') {
      const icon = new Graphics();
      icon.circle(0, 0, 4).fill({ color: 0x7C4DFF });
      icon.y = -(SPRITE_SIZE + 8);
      this.statusIcon.addChild(icon);
    }
  }

  setPosition(x: number, y: number) {
    this.curX = x;
    this.curY = y;
    this.container.x = Math.round(x);
    this.container.y = Math.round(y);
    this.pathQueue = [];
  }

  destroy() {
    this.container.destroy({ children: true });
  }
}
